import {
    CategoriesFragment,
    CategoriesQueryResult,
    PreferenceFragment,
    PreferenceQueryResult,
    SubCategoriesFragment,
} from '@graphqlTypes/sitecore';
import { DeepReadonly } from '@src/utils/type-utils';
import { DiscoverSpecialFolders } from '../enums';
import { SendPreferenceCRMData } from '@src/utils/js-bridge/js-bridge';

export function getCategoryScores(
    settingPreferenceMapping: DeepReadonly<SettingPreferenceMapping>,
    categories?: DeepReadonly<CategoriesQueryResult>
): Record<string, number> {
    const scores: Record<string, number> = {};
    const weights = calculateWeights(settingPreferenceMapping.category.length);
    categories?.Item?.data.forEach((cate) => {
        if (cate.key) {
            scores[cate.key] = getCategoryScoreByPreference(
                cate,
                settingPreferenceMapping,
                weights
            );
        }
        cate.subCat?.forEach((subcat) => {
            if (subcat.key) {
                scores[subcat.key] = getCategoryScoreByPreference(
                    subcat,
                    settingPreferenceMapping,
                    weights
                );
            }
        });
    });
    return scores;
}

export function getSettingPreferenceMapping(
    setting: DeepReadonly<SendPreferenceCRMData> | null | undefined,
    preference: DeepReadonly<PreferenceQueryResult>
): SettingPreferenceMapping {
    const level = getPreferenceByName(preference, setting?.step1 ? setting?.step1[0] : '');
    const category: DeepReadonly<PreferenceFragment>[] = [];
    setting?.step2?.forEach((name) => {
        const p = getPreferenceByName(preference, name);
        p && category.push(p);
    });

    const interest: DeepReadonly<PreferenceFragment>[] = [];

    const others: DeepReadonly<PreferenceFragment>[] = [];
    setting?.Horses?.forEach((name) => {
        const p = getPreferenceByName(preference, name);
        p && others.push(p);
    });
    setting?.Trainers?.forEach((name) => {
        const p = getPreferenceByName(preference, name);
        p && others.push(p);
    });
    setting?.Jockeys?.forEach((name) => {
        const p = getPreferenceByName(preference, name);
        p && others.push(p);
    });

    return Object.freeze({ level, category, interest, others });
}

function getCategoryScoreByPreference(
    cat: DeepReadonly<CategoriesFragment> | DeepReadonly<SubCategoriesFragment>,
    settingPreferenceMapping: DeepReadonly<SettingPreferenceMapping>,
    weights: number[]
) {
    const defaultScore = cat.defaultScore?.numberValue || 0;
    let score = 0; // score = 0 when no preference in local setting

    if (
        settingPreferenceMapping.level?.categoriesMap?.categories?.some((c) => c.name === cat.key)
    ) {
        let levelScore = 0;
        switch (settingPreferenceMapping.level.key) {
            case DiscoverSpecialFolders.novice:
                levelScore = cat.noviceScore?.numberValue || 0;
                break;
            case DiscoverSpecialFolders.intermediate:
                levelScore = cat.intermediateScore?.numberValue || 0;
                break;
            case DiscoverSpecialFolders.enthusiast:
                levelScore = cat.hardcoreScore?.numberValue || 0;
                break;
        }
        score += levelScore;
    }

    //category weight score
    settingPreferenceMapping.category.forEach((preference, i) => {
        if (preference.categoriesMap?.categories?.some((c) => c.name === cat.key)) {
            score += defaultScore * weights[i];
        }
    });

    //interest score
    settingPreferenceMapping.interest.forEach((preference) => {
        if (preference.categoriesMap?.categories?.some((c) => c.name === cat.key)) {
            score += defaultScore;
        }
    });

    //others score
    settingPreferenceMapping.others.forEach((preference) => {
        if (preference.categoriesMap?.categories?.some((c) => c.name === cat.key)) {
            score += defaultScore;
        }
    });
    return score;
}

export function getPreferenceByName(
    preference?: DeepReadonly<PreferenceQueryResult>,
    name?: string
) {
    if (name) {
        for (const folder of preference?.Item?.data || []) {
            const item = folder.content.find((preferItem) => preferItem.key === name);
            if (item && item.frontendDisplay?.boolValue) {
                return item;
            }
        }
    }
    return undefined;
}

function calculateWeights(length: number) {
    const weights = [];
    if (length) {
        const sumSeries = (length * (length + 1)) / 2;
        for (let i = length; i > 0; i--) {
            weights.push(i / sumSeries);
        }
    }
    return weights;
}

export interface SettingPreferenceMapping {
    level: DeepReadonly<PreferenceFragment> | undefined;
    category: DeepReadonly<PreferenceFragment>[];
    interest: DeepReadonly<PreferenceFragment>[];
    others: DeepReadonly<PreferenceFragment>[];
}
